// frontend/src/components/stats/GoalTimeline.jsx
//
// 2026-10-07 — "who scored, in order, and did the voice AI get it". Every
// goal of a game numbered Goal 1, Goal 2… with the scorer and assists as
// tagged, where it came from (🎙 heard by the voice AI live / 🎙 official
// pass / ✋ score button), and what was said. A goal nobody is tagged on
// says so in orange — that is the one to fix in "Fix events".
//
// Data: the game-events GET payload (events + roster). Assists are linked
// to their goal by the engine key ("<goal key>:A<number>").
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import ClipButton from './ClipButton.jsx';
import Tip from '../Tip.jsx';
import { fuzzyFilter } from '../../utils/fuzzy.js';

const mmss = (ms) => {
  if (ms == null) return null;
  const s = Math.max(0, Math.round(Number(ms) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const isGoalEvent = (e) => e?.event_type === 'goal' || (e?.event_type === 'goal_against' && !!e.engine_key);

export function buildGoals(data) {
  const events = data?.events || [];
  const roster = data?.roster || [];
  const nameOf = (uid) => {
    const p = roster.find(r => r.userId === uid);
    return p ? `${p.jerseyNumber != null ? '#' + p.jerseyNumber + ' ' : ''}${p.name || ''}`.trim() : null;
  };
  // 2026-10-19 (Press Box) — players without accounts: the roster rows carry
  // only a jersey number and a name, and the event rows only the number. A
  // number that IS on the roster must show the player, not "not on the roster".
  const byNumber = (n) => {
    if (n == null) return null;
    const p = roster.find(r => r.userId == null && String(r.jerseyNumber) === String(n));
    return p ? `#${p.jerseyNumber} ${p.name || ''}`.trim() : null;
  };
  // 2026-10-18 — a goal against counts only when the voice engine heard it
  // (engine_key); one typed into the stats grid is the goalie's GA line, not
  // an opponent goal (the score's rule: backend utils/gameStats.js).
  const goals = events.filter(isGoalEvent);
  const official = events.some(e => e.source === 'voice_final');
  // Live: the order they happened is the order they were written. After the
  // official pass the rows are rewritten at once, so tape time orders them.
  const sorted = goals.slice().sort((a, b) => {
    if (official && a.audio_offset_ms != null && b.audio_offset_ms != null) return a.audio_offset_ms - b.audio_offset_ms;
    return String(a.created_at).localeCompare(String(b.created_at));
  });
  let us = 0, them = 0;
  return sorted.map((g, i) => {
    const against = g.event_type === 'goal_against' || g.team_side === 'away';
    if (against) them += 1; else us += 1;
    const assists = g.engine_key
      ? events.filter(e => e.event_type === 'assist' && e.engine_key && e.engine_key.startsWith(g.engine_key + ':A'))
      : [];
    const voice = /^voice/.test(g.source || '');
    return {
      n: i + 1, id: g.id, against,
      scorer: against ? null : (g.user_id ? nameOf(g.user_id) : byNumber(g.jersey_number)),
      heardNumber: g.jersey_number || null,
      assists: assists.map(a => (a.user_id ? nameOf(a.user_id) : null) || byNumber(a.jersey_number) || (a.jersey_number ? `#${a.jersey_number} (not on roster)` : '?')),
      source: voice ? (g.source === 'voice_final' ? 'official' : 'voice') : 'manual',
      said: g.transcript || null,
      offsetMs: g.audio_offset_ms != null ? Number(g.audio_offset_ms) : null,
      recordingId: g.recording_id || null,
      at: mmss(g.audio_offset_ms), period: g.period || null,
      review: g.needs_review ? (g.review_reason || 'needs a look') : null,
      score: `${us}–${them}`,
    };
  });
}

const REASON = {
  unresolved_player: 'number heard is not on the roster',
  no_player_named: 'no scorer heard',
  low_confidence: 'voice AI unsure',
  no_side: 'side unknown',
  goalie_scored: 'credited to the goalie — goalies rarely score, listen and make sure',
  goalie_faceoff: "faceoff credited to the goalie — goalies don't take faceoffs; listen, or confirm they played out",
  skater_save: 'save credited to a skater — only the goalie makes saves; listen and check',
};

/**
 * 2026-10-22 (per Gilles) — reassign a goal (or any heard event) to another
 * player: a quick-search box over the roster, tap to assign. Works for
 * account players (user_id) and Press Box number-only players (jersey).
 */
export function ReassignPicker({ teamId, gameId, eventId, roster, current, onDone, onClose }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const rows = (roster || []).map(r => ({
    userId: r.userId || null, number: r.jerseyNumber ?? null, name: r.name || '',
    label: `${r.jerseyNumber != null ? '#' + r.jerseyNumber + ' ' : ''}${r.name || ''}`.trim(),
  }));
  const hits = fuzzyFilter(q, rows, (r) => [r.label, r.name, r.number != null ? String(r.number) : '']);
  const assign = async (r) => {
    setBusy(true);
    try {
      await api.patch(`/teams/${teamId}/game-events/${gameId}/${eventId}`,
        r.userId ? { user_id: r.userId } : { user_id: null, jersey_number: r.number != null ? String(r.number) : null });
      toast.success(`Assigned to ${r.label || 'player'}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not reassign');
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 p-2 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}
      onClick={(e) => e.stopPropagation()} data-reassign>
      <div className="flex items-center gap-2 mb-1">
        <input autoFocus className="input-field" style={{ padding: '6px 10px', fontSize: 12 }} placeholder="Type a number or a name…"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className="btn-ghost" style={{ padding: '4px 8px' }} onClick={onClose}>Close</button>
      </div>
      <div style={{ maxHeight: 180, overflowY: 'auto' }}>
        {hits.slice(0, 30).map((r, i) => (
          <button key={i} type="button" disabled={busy} onClick={() => assign(r)}
            className="w-full text-left text-xs px-2 py-1.5 rounded"
            style={{ background: r.label === current ? 'var(--ice-a8)' : 'transparent', color: 'var(--text)', border: 'none', cursor: 'pointer' }}>
            {r.label || '(unnamed)'}
          </button>
        ))}
        {hits.length === 0 && <div className="text-xs p-2" style={{ color: 'var(--text-3)' }}>No one matches.</div>}
      </div>
    </div>
  );
}

export default function GoalTimeline({ data, opponent = null, compact = false, teamId = null, gameId = null, canEdit = false, onChanged = null }) {
  const goals = useMemo(() => buildGoals(data), [data]);
  const [editing, setEditing] = useState(null);   // goal id whose picker is open
  if (!goals.length) return compact ? null : <div className="text-xs text-secondary">No goals yet.</div>;
  return (
    <div className="space-y-1">
      {goals.map(g => (
        <div key={g.id} className="rounded-lg px-2 py-1.5 text-xs" data-goal={g.n}
          style={{ background: 'var(--surface-1)', border: `1px solid ${!g.against && !g.scorer ? 'rgba(255,138,0,0.6)' : 'var(--border)'}` }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold" style={{ minWidth: 48 }}>Goal {g.n}</span>
            <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)' }}>{g.score}</span>
            {g.period && <span style={{ color: 'var(--text-3)' }}>P{g.period}</span>}
            {g.at && <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)' }}>{g.at}</span>}
            <span className="font-semibold" style={{ color: g.against ? 'var(--text-2)' : (g.scorer ? 'var(--text)' : 'var(--puck, #ff8a00)') }}>
              {g.against ? `${opponent || 'Opponent'} goal`
                : (g.scorer || (g.heardNumber ? `#${g.heardNumber} — not on the roster` : 'Scorer not tagged'))}
            </span>
            {!g.against && g.assists.length > 0 && <span style={{ color: 'var(--text-2)' }}>assist {g.assists.join(', ')}</span>}
            {teamId && <ClipButton teamId={teamId} recordingId={g.recordingId} offsetMs={g.offsetMs} />}
            {canEdit && !g.against && teamId && gameId && (<>
              <Tip text="This goal never happened? Delete it. The AI remembers this exact call as NOT a goal and will never re-create it — if the final score then says a goal is missing, re-run the AI and it looks for it elsewhere on the tape.">
                <button type="button" onClick={async () => {
                  if (!window.confirm(`Delete Goal ${g.n}?\n\n• It is removed for good, and the AI will NEVER re-create this exact call — it is remembered as "not a goal".\n• If the league/webcal final now says MORE goals than the tape counts, run "Re-run Press Box AI": it will look for the missing goal elsewhere on the tape.\n• No league final on file? Confirm the final score on the game page first, so the totals have something to reconcile to.`)) return;
                  try {
                    await api.delete(`/teams/${teamId}/game-events/${gameId}/${g.id}`);
                    toast.success('Goal deleted — if the totals no longer reconcile, re-run Press Box AI to find the real goal', { duration: 8000 });
                    onChanged?.();
                  } catch (e2) { toast.error(e2.response?.data?.error || 'Could not delete it'); }
                }}
                  style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                    background: 'var(--penalty-a10, rgba(255,59,59,0.1))', color: 'var(--penalty)', border: '1px solid var(--penalty)' }}>
                  Delete
                </button>
              </Tip>
              <Tip text="Assign this goal to another player — quick-search the roster and tap the right one. Your pick sticks, even when the AI re-reads the game.">
                <button type="button" onClick={() => setEditing(editing === g.id ? null : g.id)}
                  style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                    background: editing === g.id ? 'var(--gold)' : 'var(--gold-a10, rgba(240,180,41,0.12))',
                    color: editing === g.id ? '#1a1a1a' : 'var(--gold)', border: '1px solid var(--gold)' }}>
                  {editing === g.id ? 'Cancel' : 'Reassign'}
                </button>
              </Tip>
            </>)}
            <span className="ml-auto" title={g.source === 'manual' ? 'Added with the score button' : 'Picked up by the voice AI'}
              style={{ color: g.source === 'manual' ? 'var(--text-3)' : 'var(--ice)', whiteSpace: 'nowrap' }}>
              {g.source === 'manual' ? '✋ score button' : g.source === 'official' ? '🎙 voice AI · official' : '🎙 voice AI · live'}
            </span>
          </div>
          {!compact && g.said && <div style={{ color: 'var(--text-3)', fontStyle: 'italic', marginTop: 2 }}>“{g.said}”</div>}
          {g.review && !g.against && <div style={{ color: 'var(--puck, #ff8a00)', marginTop: 2 }}>⚠ {REASON[g.review] || g.review}</div>}
          {editing === g.id && (
            <ReassignPicker teamId={teamId} gameId={gameId} eventId={g.id} roster={data?.roster || []}
              current={g.scorer} onClose={() => setEditing(null)}
              onDone={() => { setEditing(null); onChanged?.(); }} />
          )}
        </div>
      ))}
    </div>
  );
}
